<template>
  <QuickViewModal :aria-label="vibe?.name || 'Вайб'" @close="$emit('close')">
    <div v-if="pending" class="vqv__loading"><Spinner /></div>
    <div v-else-if="vibe" class="vqv">
      <div class="vqv__gallery">
        <Card type="image" :slider="slider" :show-navigation="slider.length > 1" />
      </div>
      <div class="vqv__content">
        <h3 class="vqv__title">{{ vibe.name }}</h3>

        <div v-if="vibe.tags && vibe.tags.length" class="vqv__tags">
          <span v-for="tag in vibe.tags" :key="tag.id" class="vqv__tag">{{ tag.name }}</span>
        </div>

        <div v-if="vibe.description" class="vqv__desc">{{ vibe.description }}</div>
        <div v-else-if="vibe.content" class="vqv__desc" v-html="vibe.content" />

        <a :href="`/vibe/${vibe.id}`" target="_blank" rel="noopener" class="vqv__link">
          Перейти на страницу
        </a>

        <div v-if="products.length" class="vqv__products">
          <h4 class="vqv__products-title">Товары и услуги</h4>
          <div class="vqv__products-grid">
            <a
              v-for="p in products"
              :key="p.id"
              :href="`/product/${p.id}`"
              class="vqv__product-card"
              @click="onProductClick($event, p.id)"
            >
              <div class="vqv__product-img">
                <img v-if="p.mainImage?.path" :src="p.mainImage.path" :alt="p.name" loading="lazy" />
              </div>
              <div class="vqv__product-name">{{ p.name }}</div>
              <div v-if="p.price > 0" class="vqv__product-price">{{ p.price }}&nbsp;₽</div>
            </a>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="vqv__empty">Вайб не найден</div>
  </QuickViewModal>

  <ProductQuickView
    v-if="productModalId"
    :key="productModalId"
    :id="Number(productModalId)"
    @close="closeProductModal"
  />
</template>

<script setup lang="ts">
import QuickViewModal from '~/components/UI/QuickViewModal.vue'
import Card from '~/components/UI/Card/Card.vue'
import ProductQuickView from '~/components/Modals/ProductQuickView.vue'
import Spinner from '~/components/animate/SpinnerComponent.vue'
import { Page } from '~/api/Page'
import { Product } from '~/api/Product'

const props = defineProps<{ id: number }>()
defineEmits(['close'])

// useAsyncData (not top-level await) so this still works when the component
// is mounted client-side after a click, outside any <Suspense> boundary.
const { data: vibe, pending } = useAsyncData(
  () => `vibe-quickview-${props.id}`,
  () => Page.getInfo(props.id).catch(() => null)
)

const slider = computed(() =>
  ((vibe.value as any)?.files ?? [])
    .filter((f: any) => f.isImage)
    .map((f: any) => ({ id: f.id, src: f.path }))
)

// JSON round-trip strips Product's class prototype to plain data — devalue
// (Nuxt's SSR payload serializer) can't stringify class instances and would
// 500 the whole page on a direct deep-link otherwise.
const { data: productsRes } = useAsyncData(
  () => `vibe-quickview-products-${props.id}`,
  async () => {
    const res = await Product.get({ pId: props.id, limit: 12 }).catch(() => null)
    return res ? JSON.parse(JSON.stringify(res)) : null
  }
)
const products = computed(() => productsRes.value?.products ?? [])

const { activeId: productModalId, open: openProductModal, close: closeProductModal } = useQueryModal('product')
function onProductClick(e: MouseEvent, id: number) {
  if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return
  e.preventDefault()
  openProductModal(id)
}
</script>

<style scoped>
.vqv {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2.4rem;
  padding: 2.4rem;
}
.vqv__gallery {
  background: #f2f2f0;
  /* Definite height via aspect-ratio, not 100% of an auto-sized grid row —
     the latter creates a circular sizing loop that blows up to ~33554432px. */
  aspect-ratio: 3 / 4;
  max-height: 82vh;
  overflow: hidden;
  border-radius: 8px;
}
.vqv__gallery :deep(.card),
.vqv__gallery :deep(.swiper),
.vqv__gallery :deep(.swiper-slide) {
  height: 100%;
}
.vqv__gallery :deep(img) {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.vqv__content {
  padding: 2rem 0;
  display: flex;
  flex-direction: column;
  max-height: 82vh;
  overflow-y: auto;
}

.vqv__title {
  font-size: 2.4rem;
  font-weight: 500;
  color: #1b1918;
  line-height: 1.3;
  margin: 0 0 1.4rem;
}

.vqv__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  margin-bottom: 1.8rem;
}
.vqv__tag {
  font-size: 1.2rem;
  color: #888;
  background: #f3f3f3;
  border-radius: 2rem;
  padding: 0.3rem 1.2rem;
  line-height: 1.6;
}

.vqv__desc {
  font-size: 1.4rem;
  line-height: 1.65;
  color: #555;
  margin-bottom: 2rem;
}

.vqv__link {
  align-self: flex-start;
  font-size: 1.3rem;
  color: #888;
  text-decoration: underline;
  text-underline-offset: 3px;
  transition: color 0.2s;
  margin-bottom: 3.2rem;
}
.vqv__link:hover {
  color: #1b1918;
}

.vqv__products-title {
  font-size: 1.1rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #888;
  margin: 0 0 1.6rem;
}

.vqv__products-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.6rem;
}

.vqv__product-card {
  display: block;
  text-decoration: none;
  color: inherit;
  border-radius: 8px;
  overflow: hidden;
  background: #f7f7f5;
  transition: transform 0.2s ease;
}
.vqv__product-card:hover {
  transform: translateY(-2px);
}

.vqv__product-img {
  width: 100%;
  aspect-ratio: 4 / 3;
  overflow: hidden;
  background: #ebebeb;
}
.vqv__product-img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.vqv__product-name {
  padding: 1rem 1.2rem 0.2rem;
  font-size: 1.3rem;
  font-weight: 500;
  color: #1a1a1a;
  line-height: 1.3;
}

.vqv__product-price {
  padding: 0 1.2rem 1.2rem;
  font-size: 1.3rem;
  color: #666;
}

.vqv__empty,
.vqv__loading {
  padding: 6rem 4rem;
  text-align: center;
  color: #888;
  font-size: 1.5rem;
  min-width: 40rem;
  display: flex;
  align-items: center;
  justify-content: center;
}
@media (max-width: 48em) {
  .vqv__empty,
  .vqv__loading {
    min-width: 0;
  }
}

@media (max-width: 48em) {
  .vqv {
    grid-template-columns: 1fr;
    gap: 1.6rem;
    padding: 1.6rem;
  }
  .vqv__gallery {
    aspect-ratio: 4 / 3;
  }
  .vqv__content {
    padding: 1rem 0 0;
    max-height: none;
  }
  .vqv__products-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
